import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { BACKEND_URL } from "@/lib/config";
import { useAuth } from "@/lib/auth.jsx";
import { useBrand } from "@/lib/brand.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, PageHeader, Pill, SectionCard, StatCard } from "@/components/ui-bits";
import { fmtDate, formatINR, statusTone } from "@/lib/format";
import {
  Eye,
  FileUp,
  FolderOpen,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";

const LEAD_STAGES = [
  "New",
  "Attempting Contact",
  "Contacted",
  "Interested",
  "Meeting Scheduled",
  "Discovery Completed",
  "Proposal Sent",
  "Negotiation",
  "Won",
  "Lost",
];

const PARTNER_STAGES = [
  "New",
  "Attempting Contact",
  "Contacted",
  "Interested",
  "Meeting Scheduled",
];

const emptyLead = {
  name: "",
  company: "",
  contact_person: "",
  email: "",
  phone: "",
  source: "",
  stage: "New",
  brand_id: "",
};

const emptyLeadDetailForm = {
  follow_up_date: "",
  meeting_date: "",
  meeting_outcome: "",
  next_follow_up_at: "",
  value: 0,
  notes: "",
};

const dateValueForInput = (value, mode = "date") => {
  if (!value) return "";
  if (mode === "datetime") {
    return value.length >= 16 ? value.slice(0, 16) : value;
  }
  return value.slice(0, 10);
};

const detailFormFromLead = (lead) => ({
  follow_up_date: dateValueForInput(lead?.follow_up_date),
  meeting_date: dateValueForInput(lead?.meeting_date, "datetime"),
  meeting_outcome: lead?.meeting_outcome || "",
  next_follow_up_at: dateValueForInput(lead?.next_follow_up_at, "datetime"),
  value: lead?.value || 0,
  notes: lead?.notes || "",
});

const buildLeadPayload = (lead) => ({
  name: lead?.name || "",
  company: lead?.company || "",
  contact_person: lead?.contact_person || lead?.name || "",
  email: lead?.email || null,
  phone: lead?.phone || "",
  source: lead?.source || "",
  stage: lead?.stage || "New",
  brand_id: lead?.brand_id || "",
  follow_up_date: lead?.follow_up_date || null,
  meeting_date: lead?.meeting_date || null,
  meeting_outcome: lead?.meeting_outcome || "",
  next_follow_up_at: lead?.next_follow_up_at || null,
  value: Number(lead?.value || 0),
  notes: lead?.notes || "",
});

const leadMatchesDate = (lead, date) => {
  if (!date) return true;
  return [
    lead.created_at,
    lead.follow_up_date,
    lead.meeting_date,
    lead.next_follow_up_at,
  ].some((value) => value && String(value).slice(0, 10) === date);
};

export default function Leads() {
  const { isAdmin, isPartner } = useAuth();
  const { brands, activeBrandId } = useBrand();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyLead);
  const [detailForm, setDetailForm] = useState(emptyLeadDetailForm);
  const [filters, setFilters] = useState({
    search: "",
    stage: "all",
    company: "",
    contactPerson: "",
    date: "",
    brandId: "all",
  });
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [stageDraft, setStageDraft] = useState("New");
  const [timelineDraft, setTimelineDraft] = useState({ activity: "", comment: "" });
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [detailSaving, setDetailSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const stageOptions = isAdmin ? LEAD_STAGES : PARTNER_STAGES;
  const apiBrandId = filters.brandId !== "all"
    ? filters.brandId
    : activeBrandId && activeBrandId !== "all"
      ? activeBrandId
      : undefined;

  const refreshLeads = async () => {
    const response = await api.get("/leads", { params: apiBrandId ? { brand_id: apiBrandId } : {} });
    setLeads(response.data || []);
    return response.data || [];
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .get("/leads", { params: apiBrandId ? { brand_id: apiBrandId } : {} })
      .then((r) => {
        if (!active) return;
        setLeads(r.data || []);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [apiBrandId]);

  const visibleLeads = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    return (leads || []).filter((lead) => {
      const company = (lead.company || "").toLowerCase();
      const contactPerson = (lead.contact_person || lead.name || "").toLowerCase();
      const brandOk = filters.brandId === "all" || lead.brand_id === filters.brandId;
      const stageOk = filters.stage === "all" || lead.stage === filters.stage;
      const companyOk = !filters.company || company.includes(filters.company.toLowerCase());
      const contactOk = !filters.contactPerson || contactPerson.includes(filters.contactPerson.toLowerCase());
      const searchOk =
        !term ||
        [lead.name, lead.company, lead.contact_person, lead.email, lead.phone]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      return brandOk && stageOk && companyOk && contactOk && searchOk && leadMatchesDate(lead, filters.date);
    });
  }, [filters, leads]);

  const selectedLead = useMemo(
    () => visibleLeads.find((lead) => lead.id === selectedLeadId) || leads.find((lead) => lead.id === selectedLeadId) || null,
    [visibleLeads, leads, selectedLeadId]
  );

  useEffect(() => {
    if (!leads.length) {
      setSelectedLeadId(null);
      setDetailOpen(false);
      return;
    }
    if (selectedLeadId && !leads.some((lead) => lead.id === selectedLeadId)) {
      setSelectedLeadId(null);
      setDetailOpen(false);
    }
  }, [leads, selectedLeadId]);

  useEffect(() => {
    if (!selectedLead) return;
    setStageDraft(selectedLead.stage || "New");
  }, [selectedLead]);

  const summary = useMemo(() => {
    const total = visibleLeads.length;
    const won = visibleLeads.filter((lead) => lead.stage === "Won").length;
    const lost = visibleLeads.filter((lead) => lead.stage === "Lost").length;
    const meetingScheduled = visibleLeads.filter((lead) => lead.stage === "Meeting Scheduled").length;
    const followUpPending = visibleLeads.filter((lead) => {
      const next = lead.next_follow_up_at || lead.follow_up_date;
      return next && String(next).slice(0, 10) <= new Date().toISOString().slice(0, 10) && !["Won", "Lost"].includes(lead.stage);
    }).length;
    return { total, won, lost, meetingScheduled, followUpPending };
  }, [visibleLeads]);

  const openCreate = () => {
    const fallbackBrandId =
      apiBrandId ||
      (activeBrandId && activeBrandId !== "all" ? activeBrandId : brands[0]?.id || "");
    setEditing(null);
    setForm({
      ...emptyLead,
      brand_id: fallbackBrandId,
    });
    setOpen(true);
  };

  const openEdit = (lead) => {
    setEditing(lead);
    setForm({
      ...emptyLead,
      ...lead,
      email: lead.email || "",
      contact_person: lead.contact_person || "",
    });
    setOpen(true);
  };

  const reloadSelectedLead = async (leadId) => {
    const { data } = await api.get(`/leads/${leadId}`);
    setLeads((items) => items.map((item) => (item.id === leadId ? data : item)));
    return data;
  };

  const openLeadDetail = async (lead) => {
    setSelectedLeadId(lead.id);
    setStageDraft(lead.stage || "New");
    setDetailForm(detailFormFromLead(lead));
    setTimelineDraft({ activity: "", comment: "" });
    setDetailOpen(true);
    try {
      const refreshed = await reloadSelectedLead(lead.id);
      setStageDraft(refreshed.stage || "New");
      setDetailForm(detailFormFromLead(refreshed));
    } catch {
      // Keep the currently loaded data if the background refresh fails.
    }
  };

  const submitLead = async () => {
    setSubmitting(true);
    try {
      const payload = buildLeadPayload({
        ...(editing || {}),
        ...form,
        email: form.email || null,
        contact_person: form.contact_person || form.name,
      });
      if (editing) {
        await api.put(`/leads/${editing.id}`, payload);
        toast.success("Lead updated");
      } else {
        const created = await api.post("/leads", payload);
        toast.success("Lead created");
        if (created.data?.id) {
          setSelectedLeadId(created.data.id);
        }
      }
      setOpen(false);
      await refreshLeads();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to save lead");
    } finally {
      setSubmitting(false);
    }
  };

  const saveLeadDetails = async () => {
    if (!selectedLead) return;
    setDetailSaving(true);
    try {
      const payload = buildLeadPayload({
        ...selectedLead,
        ...detailForm,
        follow_up_date: detailForm.follow_up_date || null,
        meeting_date: detailForm.meeting_date || null,
        next_follow_up_at: detailForm.next_follow_up_at || null,
        value: Number(detailForm.value || 0),
      });
      await api.put(`/leads/${selectedLead.id}`, payload);
      const refreshed = await reloadSelectedLead(selectedLead.id);
      setDetailForm(detailFormFromLead(refreshed));
      toast.success("Lead details updated");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update lead details");
    } finally {
      setDetailSaving(false);
    }
  };

  const addTimelineEntry = async () => {
    if (!selectedLead || !timelineDraft.activity.trim()) return;
    try {
      await api.post(`/leads/${selectedLead.id}/timeline`, timelineDraft);
      await reloadSelectedLead(selectedLead.id);
      setTimelineDraft({ activity: "", comment: "" });
      toast.success("Timeline updated");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update timeline");
    }
  };

  const uploadAttachment = async (file) => {
    if (!selectedLead || !file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      await api.post(`/leads/${selectedLead.id}/attachments`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await reloadSelectedLead(selectedLead.id);
      toast.success("Attachment uploaded");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not upload attachment");
    } finally {
      setUploading(false);
    }
  };

  const applyStageChange = async ({ convert = false } = {}) => {
    if (!selectedLead) return;
    try {
      if (!convert && stageDraft !== selectedLead.stage) {
        await api.patch(`/leads/${selectedLead.id}/stage`, { stage: stageDraft, comment: "" });
      }
      if (convert) {
        await api.post(`/leads/${selectedLead.id}/convert`, { create_project: true });
        toast.success("Lead converted into Client and Project");
      } else {
        toast.success("Lead stage updated");
      }
      const refreshed = await reloadSelectedLead(selectedLead.id);
      setStageDraft(refreshed.stage || "New");
      setConvertDialogOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update stage");
    }
  };

  const handleStageSave = async () => {
    if (!selectedLead || stageDraft === selectedLead.stage) return;
    if (isAdmin && stageDraft === "Won") {
      setConvertDialogOpen(true);
      return;
    }
    await applyStageChange();
  };

  const deleteLead = async (lead) => {
    if (!lead) return;
    if (!window.confirm(`Delete ${lead.company || lead.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      setLeads((items) => items.filter((item) => item.id !== lead.id));
      if (selectedLeadId === lead.id) {
        setSelectedLeadId(null);
        setDetailOpen(false);
      }
      toast.success("Lead deleted");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not delete lead");
    }
  };

  const selectedLeadBrand = selectedLead ? brands.find((brand) => brand.id === selectedLead.brand_id) : null;

  return (
    <div data-testid="leads-page">
      <PageHeader
        eyebrow="Pipeline"
        title="Leads"
        description={
          isPartner
            ? "Manage only your assigned leads, meetings, notes, and next actions."
            : "Oversee the full partner-to-admin lead lifecycle across brands."
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => refreshLeads()}
              className="border-border bg-white text-slate-700 hover:bg-[#f8f6f2]"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
            <Button data-testid="new-lead-btn" onClick={openCreate} className="bg-bronze text-black hover:bg-[#d4af68]">
              <Plus className="w-4 h-4 mr-1.5" /> New lead
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard label="Visible Leads" value={summary.total} accent />
        <StatCard label="Meeting Scheduled" value={summary.meetingScheduled} />
        <StatCard label="Follow-up Pending" value={summary.followUpPending} />
        <StatCard label="Won" value={summary.won} />
        <StatCard label="Lost" value={summary.lost} />
      </div>

      <SectionCard title="Search & Filters" className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
          <div className="xl:col-span-2">
            <Label>Search</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                value={filters.search}
                onChange={(e) => setFilters((curr) => ({ ...curr, search: e.target.value }))}
                placeholder="Search lead, company, contact, email, phone"
                className="pl-9 bg-white"
              />
            </div>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={filters.stage} onValueChange={(value) => setFilters((curr) => ({ ...curr, stage: value }))}>
              <SelectTrigger className="mt-1 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {LEAD_STAGES.map((stage) => (
                  <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Brand</Label>
            <Select value={filters.brandId} onValueChange={(value) => setFilters((curr) => ({ ...curr, brandId: value }))}>
              <SelectTrigger className="mt-1 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All visible brands</SelectItem>
                {brands.map((brand) => (
                  <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Company</Label>
            <Input
              value={filters.company}
              onChange={(e) => setFilters((curr) => ({ ...curr, company: e.target.value }))}
              className="mt-1 bg-white"
            />
          </div>
          <div>
            <Label>Contact Person</Label>
            <Input
              value={filters.contactPerson}
              onChange={(e) => setFilters((curr) => ({ ...curr, contactPerson: e.target.value }))}
              className="mt-1 bg-white"
            />
          </div>
          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={filters.date}
              onChange={(e) => setFilters((curr) => ({ ...curr, date: e.target.value }))}
              className="mt-1 bg-white"
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard title={`Lead List (${visibleLeads.length})`}>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading leads…</div>
        ) : visibleLeads.length === 0 ? (
          <EmptyState
            title="No leads found"
            description="Adjust your filters or create a new lead to start the workflow."
            action={
              <Button onClick={openCreate} className="bg-bronze text-black hover:bg-[#d4af68]">
                <Plus className="w-4 h-4 mr-1.5" /> Add lead
              </Button>
            }
          />
        ) : (
          <Table className="min-w-[980px]">
            <TableHeader>
              <TableRow>
                <TableHead>Lead</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Meeting</TableHead>
                <TableHead>Follow-up</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleLeads.map((lead) => {
                const brand = brands.find((item) => item.id === lead.brand_id);
                return (
                  <TableRow
                    key={lead.id}
                    onClick={() => openLeadDetail(lead)}
                    className={`cursor-pointer ${lead.id === selectedLeadId && detailOpen ? "bg-[#fcf8ef]" : "bg-white"}`}
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <TableCell>
                      <div className="font-medium text-slate-900">{lead.company || lead.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {lead.contact_person || lead.name} · {lead.email || lead.phone || "No contact added"}
                      </div>
                    </TableCell>
                    <TableCell>{brand ? <Pill>{brand.code}</Pill> : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell><Pill tone={statusTone(lead.stage)}>{lead.stage}</Pill></TableCell>
                    <TableCell>{lead.lead_owner_name || "—"}</TableCell>
                    <TableCell>{lead.meeting_date ? fmtDate(lead.meeting_date) : "—"}</TableCell>
                    <TableCell>{lead.next_follow_up_at ? fmtDate(lead.next_follow_up_at) : lead.follow_up_date ? fmtDate(lead.follow_up_date) : "—"}</TableCell>
                    <TableCell className="text-right font-medium text-slate-900">{formatINR(lead.value)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            openLeadDetail(lead);
                          }}
                          className="border-border bg-white text-slate-700 hover:bg-[#f8f6f2]"
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" /> View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(lead);
                          }}
                          className="border-border bg-white text-slate-700 hover:bg-[#f8f6f2]"
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                        </Button>
                        {isAdmin ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteLead(lead);
                            }}
                            className="border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="lead-dialog" className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit lead" : "Create lead"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Prospect Name</Label>
              <Input value={form.name} onChange={(e) => setForm((curr) => ({ ...curr, name: e.target.value }))} className="mt-1 bg-white" />
            </div>
            <div>
              <Label>Company</Label>
              <Input value={form.company} onChange={(e) => setForm((curr) => ({ ...curr, company: e.target.value }))} className="mt-1 bg-white" />
            </div>
            <div>
              <Label>Contact Person</Label>
              <Input value={form.contact_person} onChange={(e) => setForm((curr) => ({ ...curr, contact_person: e.target.value }))} className="mt-1 bg-white" />
            </div>
            <div>
              <Label>Brand</Label>
              <Select value={form.brand_id || ""} onValueChange={(value) => setForm((curr) => ({ ...curr, brand_id: value }))}>
                <SelectTrigger className="mt-1 bg-white">
                  <SelectValue placeholder="Select brand" />
                </SelectTrigger>
                <SelectContent>
                  {brands.map((brand) => (
                    <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => setForm((curr) => ({ ...curr, email: e.target.value }))} className="mt-1 bg-white" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm((curr) => ({ ...curr, phone: e.target.value }))} className="mt-1 bg-white" />
            </div>
            <div>
              <Label>Source</Label>
              <Input value={form.source} onChange={(e) => setForm((curr) => ({ ...curr, source: e.target.value }))} className="mt-1 bg-white" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.stage} onValueChange={(value) => setForm((curr) => ({ ...curr, stage: value }))}>
                <SelectTrigger className="mt-1 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {stageOptions.map((stage) => (
                    <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submitLead} disabled={submitting} className="bg-bronze text-black hover:bg-[#d4af68]">
              {submitting ? "Saving…" : editing ? "Save Changes" : "Create Lead"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {selectedLead ? selectedLead.company || selectedLead.name : "Lead detail"}
            </DialogTitle>
          </DialogHeader>
          {!selectedLead ? (
            <EmptyState
              title="Lead not available"
              description="Refresh the list and open the record again."
            />
          ) : (
            <div className="space-y-6">
              <div className="rounded-xl border border-border bg-[#fafaf9] p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <div className="text-lg font-display font-semibold text-slate-900">
                        {selectedLead.company || selectedLead.name}
                      </div>
                      <Pill tone={statusTone(selectedLead.stage)}>{selectedLead.stage}</Pill>
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      {selectedLead.contact_person || selectedLead.name} · {selectedLead.email || selectedLead.phone || "No contact added"}
                    </div>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Lead Owner</div>
                        <div className="mt-1 text-slate-900 flex items-center gap-2">
                          <UserRound className="w-4 h-4 text-bronze" /> {selectedLead.lead_owner_name || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Brand</div>
                        <div className="mt-1 text-slate-900">{selectedLeadBrand?.name || "—"}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Source</div>
                        <div className="mt-1 text-slate-900">{selectedLead.source || "—"}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Created</div>
                        <div className="mt-1 text-slate-900">{selectedLead.created_at ? fmtDate(selectedLead.created_at) : "—"}</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(selectedLead)}
                      className="border-border bg-white text-slate-700 hover:bg-[#f8f6f2]"
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit basic info
                    </Button>
                    {isAdmin ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteLead(selectedLead)}
                        className="border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-[1.2fr,0.8fr] gap-6">
                <div className="space-y-6">
                  <div className="rounded-xl border border-border bg-white p-5">
                    <div className="text-sm font-medium text-slate-900">Lead Details</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Add meeting outcomes, follow-up dates, notes, and value after the lead is created.
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label>Meeting Date</Label>
                        <Input
                          type="datetime-local"
                          value={detailForm.meeting_date}
                          onChange={(e) => setDetailForm((curr) => ({ ...curr, meeting_date: e.target.value }))}
                          className="mt-1 bg-[#fafaf9]"
                        />
                      </div>
                      <div>
                        <Label>Next Follow-up</Label>
                        <Input
                          type="datetime-local"
                          value={detailForm.next_follow_up_at}
                          onChange={(e) => setDetailForm((curr) => ({ ...curr, next_follow_up_at: e.target.value }))}
                          className="mt-1 bg-[#fafaf9]"
                        />
                      </div>
                      <div>
                        <Label>Fallback Follow-up Date</Label>
                        <Input
                          type="date"
                          value={detailForm.follow_up_date}
                          onChange={(e) => setDetailForm((curr) => ({ ...curr, follow_up_date: e.target.value }))}
                          className="mt-1 bg-[#fafaf9]"
                        />
                      </div>
                      <div>
                        <Label>Estimated Value (₹)</Label>
                        <Input
                          type="number"
                          value={detailForm.value}
                          onChange={(e) => setDetailForm((curr) => ({ ...curr, value: e.target.value }))}
                          className="mt-1 bg-[#fafaf9]"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Meeting Outcome</Label>
                        <Textarea
                          value={detailForm.meeting_outcome}
                          onChange={(e) => setDetailForm((curr) => ({ ...curr, meeting_outcome: e.target.value }))}
                          rows={3}
                          className="mt-1 bg-[#fafaf9]"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Latest Context Note</Label>
                        <Textarea
                          value={detailForm.notes}
                          onChange={(e) => setDetailForm((curr) => ({ ...curr, notes: e.target.value }))}
                          rows={4}
                          className="mt-1 bg-[#fafaf9]"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <Button onClick={saveLeadDetails} disabled={detailSaving} className="bg-bronze text-black hover:bg-[#d4af68]">
                        {detailSaving ? "Saving…" : "Save lead details"}
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-xl border border-border bg-white p-5">
                    <div className="text-sm font-medium text-slate-900">Append-only Timeline</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Notes are preserved as activity entries and never overwrite prior history.
                    </div>
                    <div className="grid grid-cols-1 gap-3 mt-4">
                      <div>
                        <Label>Activity</Label>
                        <Input
                          value={timelineDraft.activity}
                          onChange={(e) => setTimelineDraft((curr) => ({ ...curr, activity: e.target.value }))}
                          placeholder="Called client, WhatsApp sent, Requirements received…"
                          className="mt-1 bg-[#fafaf9]"
                        />
                      </div>
                      <div>
                        <Label>Comment</Label>
                        <Textarea
                          value={timelineDraft.comment}
                          onChange={(e) => setTimelineDraft((curr) => ({ ...curr, comment: e.target.value }))}
                          rows={3}
                          className="mt-1 bg-[#fafaf9]"
                        />
                      </div>
                      <div>
                        <Button onClick={addTimelineEntry} className="bg-bronze text-black hover:bg-[#d4af68]">
                          Add timeline entry
                        </Button>
                      </div>
                    </div>
                    <div className="mt-5 space-y-3">
                      {(selectedLead.timeline || []).length === 0 ? (
                        <div className="rounded-lg border border-dashed border-border bg-[#fafaf9] p-4 text-sm text-muted-foreground">
                          No timeline activity yet.
                        </div>
                      ) : (
                        [...(selectedLead.timeline || [])].reverse().map((entry) => (
                          <div key={entry.id} className="rounded-lg border border-border bg-[#fafaf9] p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="text-sm font-medium text-slate-900">{entry.activity}</div>
                                {entry.comment ? <div className="mt-1 text-sm text-slate-700">{entry.comment}</div> : null}
                              </div>
                              <div className="text-right text-xs text-muted-foreground shrink-0">
                                <div>{entry.date} {entry.time}</div>
                                <div className="mt-1">{entry.user_name || "System"}</div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="rounded-xl border border-border bg-white p-5">
                    <div className="text-sm font-medium text-slate-900">Stage Control</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {isPartner
                        ? "Partners can only manage early-stage progression up to Meeting Scheduled."
                        : "Admin controls the post-meeting stages, closing, and conversion."}
                    </div>
                    <div className="mt-4 flex flex-col gap-3">
                      <Select value={stageDraft} onValueChange={setStageDraft}>
                        <SelectTrigger className="bg-[#fafaf9]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {stageOptions.map((stage) => (
                            <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        onClick={handleStageSave}
                        className="bg-bronze text-black hover:bg-[#d4af68]"
                        disabled={stageDraft === selectedLead.stage}
                      >
                        Save stage
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-xl border border-border bg-white p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-slate-900">Attachments</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Visiting cards, client documents, and supporting files stay attached to the lead record.
                        </div>
                      </div>
                      <label className="inline-flex items-center gap-2 rounded-lg border border-border bg-[#fafaf9] px-3 py-2 text-sm text-slate-700 hover:bg-[#f5f2eb] cursor-pointer">
                        <FileUp className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload file"}
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => uploadAttachment(e.target.files?.[0])}
                          disabled={uploading}
                        />
                      </label>
                    </div>
                    <div className="mt-4 space-y-3">
                      {(selectedLead.attachments || []).length === 0 ? (
                        <div className="rounded-lg border border-dashed border-border bg-[#fafaf9] p-4 text-sm text-muted-foreground">
                          No files uploaded yet.
                        </div>
                      ) : (
                        (selectedLead.attachments || []).map((item) => (
                          <a
                            key={item.id}
                            href={`${BACKEND_URL}${item.file_url}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-between rounded-lg border border-border bg-[#fafaf9] p-3 hover:bg-[#f5f2eb]"
                          >
                            <div>
                              <div className="text-sm font-medium text-slate-900">{item.file_name}</div>
                              <div className="text-xs text-muted-foreground">
                                Uploaded by {item.uploaded_by_user_name || "Unknown"} on {fmtDate(item.created_at)}
                              </div>
                            </div>
                            <FolderOpen className="w-4 h-4 text-bronze" />
                          </a>
                        ))
                      )}
                    </div>
                  </div>

                  {selectedLead.converted_client_id || selectedLead.converted_project_id ? (
                    <div className="rounded-xl border border-[#c5a059]/30 bg-[#fef7ee] p-5">
                      <div className="text-sm font-medium text-slate-900">Conversion Provenance</div>
                      <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-700">
                        <div>Client ID: {selectedLead.converted_client_id || "—"}</div>
                        <div>Project ID: {selectedLead.converted_project_id || "—"}</div>
                        <div>Converted At: {selectedLead.converted_at ? fmtDate(selectedLead.converted_at) : "—"}</div>
                        <div>Invoice Generated: {selectedLead.invoice_generated ? "Yes" : "No"}</div>
                        <div>Invoice Paid: {selectedLead.invoice_paid ? "Yes" : "No"}</div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Convert this lead into Client and Project?</AlertDialogTitle>
            <AlertDialogDescription>
              Marking the lead as Won will create the linked Client and Project records while preserving the lead history and commission provenance fields.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-bronze text-black hover:bg-[#d4af68]"
              onClick={(e) => {
                e.preventDefault();
                applyStageChange({ convert: true });
              }}
            >
              Confirm conversion
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
